/**
 * ReminderBell — header-mounted bell icon that:
 *   • Shows a badge with pending-reminder count.
 *   • Opens a side-drawer listing all pending + recent fired reminders.
 *   • Has a "+ New" button that launches ReminderModal.
 *   • Fires an in-app toast + sound when a pending reminder's fire_at passes
 *     while the tab is open (in case the user denied browser push).
 *
 * Available to every role — reminders are personal.
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, Plus, Trash2, X, Check, CalendarClock, AlarmClock } from 'lucide-react';
import ReminderModal from './ReminderModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ReminderBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const firedLocallyRef = useRef(new Set());   // dedupe local toasts within a session

  const pendingCount = items.filter((r) => r.status === 'pending').length;

  const load = async () => {
    try {
      const res = await axios.get(`${API}/reminders?include_fired=true`, { withCredentials: true });
      setItems(res.data || []);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  // Local fallback: poll every 15s and show a toast for any reminder that
  // transitioned past its fire_at and we haven't toasted yet. The real push
  // comes via FCM from the backend scheduler; this is an extra safety net
  // so the user is never caught unaware while the tab is open.
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      items.forEach((r) => {
        if (r.status !== 'pending') return;
        const fa = new Date(r.fire_at);
        if (fa <= now && !firedLocallyRef.current.has(r.id)) {
          firedLocallyRef.current.add(r.id);
          toast(`⏰ ${r.title}`, { description: r.body || 'Reminder', duration: 15000 });
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA='); audio.play().catch(() => {});
          } catch { /* noop */ }
        }
      });
    };
    const t = setInterval(tick, 15000);
    tick();
    return () => clearInterval(t);
  }, [items]);

  const dismiss = async (r) => {
    try {
      await axios.post(`${API}/reminders/${r.id}/dismiss`, {}, { withCredentials: true });
      load();
    } catch (e) { toast.error('Failed'); }
  };
  const snooze = async (r, mins) => {
    try {
      await axios.post(`${API}/reminders/${r.id}/snooze`, { minutes: mins }, { withCredentials: true });
      toast.success(`Snoozed ${mins} min`);
      load();
    } catch (e) { toast.error('Failed'); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete reminder "${r.title}"?`)) return;
    try {
      await axios.delete(`${API}/reminders/${r.id}`, { withCredentials: true });
      load();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <>
      <div className="relative">
        <button
          data-testid="reminder-bell"
          onClick={() => setOpen((v) => !v)}
          className="relative p-2 rounded-lg text-muted-foreground hover:bg-secondary transition-colors"
          title="Reminders"
        >
          <AlarmClock size={16} />
          {pendingCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-black text-[9px] font-bold flex items-center justify-center" data-testid="reminder-badge">
              {pendingCount > 9 ? '9+' : pendingCount}
            </span>
          )}
        </button>

        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <div className="absolute right-0 top-10 w-80 sm:w-96 max-h-[75vh] overflow-hidden z-50 bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl flex flex-col" data-testid="reminder-drawer">
              <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
                <div className="text-zinc-50 text-sm font-semibold flex items-center gap-2">
                  <AlarmClock size={14} className="text-amber-400" /> Reminders
                </div>
                <button
                  onClick={() => { setEditing(null); setShowModal(true); setOpen(false); }}
                  className="text-xs px-2 py-1 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-lg flex items-center gap-1"
                  data-testid="reminder-new-btn"
                >
                  <Plus size={12} /> New
                </button>
              </div>
              <div className="overflow-y-auto flex-1 p-2 space-y-1.5">
                {items.length === 0 && (
                  <div className="text-center text-zinc-500 text-xs py-8">
                    No reminders yet. Tap <span className="text-amber-400">+ New</span> to create one.
                  </div>
                )}
                {items.map((r) => <ReminderCard key={r.id} r={r} onDismiss={dismiss} onSnooze={snooze} onDelete={del} onEdit={() => { setEditing(r); setShowModal(true); setOpen(false); }} />)}
              </div>
            </div>
          </>
        )}
      </div>

      {showModal && (
        <ReminderModal
          editing={editing}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={() => { setShowModal(false); setEditing(null); load(); }}
        />
      )}
    </>
  );
}

function ReminderCard({ r, onDismiss, onSnooze, onDelete, onEdit }) {
  const fa = new Date(r.fire_at);
  const now = new Date();
  const diffMs = fa - now;
  const diffMin = Math.round(diffMs / 60000);
  const overdue = diffMs <= 0 && r.status === 'pending';
  const fmt = fa.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  const relative =
    r.status === 'fired' ? 'fired'
    : r.status === 'dismissed' ? 'dismissed'
    : overdue ? `due ${Math.abs(diffMin)}m ago`
    : diffMin < 60 ? `in ${diffMin}m`
    : diffMin < 60 * 24 ? `in ${Math.round(diffMin / 60)}h`
    : `in ${Math.round(diffMin / (60 * 24))}d`;
  const borderColor = r.status === 'pending' ? (overdue ? 'border-red-700 bg-red-900/10' : 'border-zinc-800') : 'border-zinc-800 opacity-60';
  return (
    <div className={`rounded-xl border p-2.5 ${borderColor}`} data-testid={`reminder-card-${r.id}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-zinc-100 text-xs font-semibold truncate">{r.title}</div>
          {r.body && <div className="text-zinc-500 text-[11px] truncate">{r.body}</div>}
          <div className="flex items-center gap-1.5 mt-1 text-[10px] text-zinc-500">
            <CalendarClock size={10} /> {fmt}
            <span className={overdue ? 'text-red-400' : 'text-amber-400'}>· {relative}</span>
            {r.source === 'voice' && <span className="text-blue-400">· voice</span>}
            {r.source?.startsWith('ti_') && <span className="text-sky-400">· TI</span>}
            {r.google_event_link && <a href={r.google_event_link} target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline" data-testid={`gcal-link-${r.id}`}>· 📅 Calendar</a>}
            {r.google_task_id && <a href={r.google_task_link || 'https://tasks.google.com/'} target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline" data-testid={`gtask-link-${r.id}`}>· ✓ Task</a>}
            {r.google_error && <span className="text-rose-400" title={r.google_error}>· Google sync failed</span>}
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          {r.status === 'pending' && (
            <>
              <button onClick={() => onSnooze(r, 10)} className="text-zinc-500 hover:text-amber-400 p-1" title="Snooze 10 min" data-testid={`snooze-${r.id}`}>
                <Clock size={11} />
              </button>
              <button onClick={() => onDismiss(r)} className="text-zinc-500 hover:text-green-400 p-1" title="Mark done" data-testid={`dismiss-${r.id}`}>
                <Check size={11} />
              </button>
            </>
          )}
          <button onClick={onEdit} className="text-zinc-500 hover:text-blue-400 p-1" title="Edit" data-testid={`edit-rem-${r.id}`}>
            <X size={11} className="rotate-45" />
          </button>
          <button onClick={() => onDelete(r)} className="text-zinc-500 hover:text-red-400 p-1" title="Delete" data-testid={`delete-rem-${r.id}`}>
            <Trash2 size={11} />
          </button>
        </div>
      </div>
    </div>
  );
}
